import { botName } from '@halvard/ai';
import {
  CENTER_SUMMIT,
  HalvardBoard,
  pieceName,
  type CameraView,
  type Square,
  type SummitMark,
} from '@halvard/board3d';
import {
  squareName,
  summitsOf,
  type SummitMode,
  visionFor,
  type GameState,
  type LogEntry,
  type PieceType,
} from '@halvard/rules';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useStore } from 'zustand';
import { PieceGlyph } from '../components/PieceGlyph';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { availableActions, toHints, type MasonMode } from './hints';
import { EXPERIMENTS, isOn, mountainName, type ExperimentKey } from './mountain';
import { PIECE_RULES, resultText, TEAM_LABEL } from './pieceInfo';
import {
  isBot,
  shownGame,
  soloHuman,
  viewerOf,
  type LocalGameStoreApi,
  type Players,
} from './store';
import './LocalGame.css';

const VIEWS: { id: CameraView; label: string }[] = [
  { id: 'ivory', label: 'Ivory side' },
  { id: 'basalt', label: 'Basalt side' },
  { id: 'top', label: 'Top' },
];

const MASON_MODES: { id: MasonMode; label: string }[] = [
  { id: 'move', label: 'Move' },
  { id: 'raise', label: 'Raise' },
  { id: 'lower', label: 'Lower' },
];

const TYPE_ORDER: PieceType[] = ['B', 'C', 'X', 'L', 'M'];

/** Summit markers for the board: e5, or one per camp in its owner's colour. */
function summitMarks(mode: SummitMode): readonly SummitMark[] {
  return mode === 'center'
    ? CENTER_SUMMIT
    : summitsOf(mode).map((s) => ({ ...s, team: s.r < 4 ? ('B' as const) : ('I' as const) }));
}

/** A move as the viewer sees it, with what fog changed. */
function entryText(entry: LogEntry): string {
  if (entry.blocked) return `${entry.notation} (blocked)`;
  if (entry.result?.kind === 'capture') return `${entry.notation} (captured)`;
  if (entry.result)
    return `${entry.notation} (stopped at ${squareName(entry.result.r, entry.result.c)})`;
  return entry.notation;
}

/** Squares of the last action: where the piece stood and the square it acted on. */
function lastMoveSquares(game: GameState, previous: GameState | undefined): Square[] {
  const entry = game.log[game.log.length - 1];
  const before = previous?.pieces.find((p) => p.id === entry?.pieceId);
  if (!entry || !before || entry.notation === '?') return [];
  return [
    { r: before.r, c: before.c },
    { r: entry.action.r, c: entry.action.c },
  ];
}

export interface LocalGameProps {
  store: LocalGameStoreApi;
  onNewMountain: () => void;
  onClassic: () => void;
  /** Experimental rule options, shown as toggle buttons. */
  onToggleExperiment: (key: ExperimentKey) => void;
  /** Extra controls at the top of the panel (bot games). */
  panelExtra?: ReactNode;
}

/** "You", "Ivory player", or the bot's name. */
function playerLabel(players: Players, team: 'I' | 'B'): string {
  const player = players[team];
  if (player.kind === 'bot') return botName(player.spec);
  return soloHuman(players) === team ? 'You' : `${TEAM_LABEL[team]} player`;
}

export function LocalGame(props: LocalGameProps) {
  const { store, onNewMountain, onClassic, onToggleExperiment, panelExtra } = props;
  const s = useStore(store);
  const reducedMotion = useReducedMotion();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmResign, setConfirmResign] = useState(false);
  const moveList = useRef<HTMLOListElement>(null);
  // Under fog, everything below reads the viewer's view, not the real board.
  const game = shownGame(s);
  const over = game.winReason !== null;
  const fogged = game !== s.game;
  const viewer = viewerOf(s);
  const visible = useMemo(
    () => (fogged && viewer ? visionFor(s.game, viewer) : undefined),
    [fogged, viewer, s.game],
  );
  const anyBot = isBot(s.players, 'I') || isBot(s.players, 'B');
  const botsOnly = isBot(s.players, 'I') && isBot(s.players, 'B');
  const humanTurn = !isBot(s.players, game.turn);

  const selected = game.pieces.find((p) => p.id === s.selectedId && p.alive);
  const hints = useMemo(
    () => (over || !humanTurn ? [] : toHints(availableActions(game, selected, s.masonMode))),
    [game, selected, s.masonMode, over, humanTurn],
  );
  const lastMove = useMemo(
    () => lastMoveSquares(game, s.history[s.history.length - 1]),
    [game, s.history],
  );
  const facing = s.view === 'basalt' ? 'B' : 'I';
  const result = over && game.winReason ? resultText(game.winner, game.winReason) : null;
  const canActWithSelected = selected !== undefined && selected.team === game.turn && !over;

  const moves = useMemo(() => {
    const rows: { no: number; ivory?: string; basalt?: string }[] = [];
    for (const entry of game.log) {
      let row = rows[rows.length - 1];
      if (!row || row.no !== entry.moveNo) {
        row = { no: entry.moveNo };
        rows.push(row);
      }
      if (entry.team === 'I') row.ivory = entryText(entry);
      else row.basalt = entryText(entry);
    }
    return rows;
  }, [game.log]);

  // Keep the latest move in view.
  useEffect(() => {
    const list = moveList.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [moves]);

  const captured = (team: 'I' | 'B') =>
    game.pieces
      .filter((p) => !p.alive && p.team === team)
      .sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type));

  const status = result
    ? result.title
    : anyBot
      ? `${playerLabel(s.players, game.turn)}${humanTurn ? ' to move' : s.thinking ? ' is thinking…' : ' to move'}`
      : `${TEAM_LABEL[game.turn]} to move`;
  const resigning = soloHuman(s.players) ?? game.turn;

  return (
    <div className="game-layout">
      {s.curtain && (
        <div
          className="game-curtain"
          role="dialog"
          aria-modal="true"
          aria-labelledby="curtain-title"
        >
          <div className="game-curtainCard">
            <PieceGlyph team={game.turn} type="B" size={40} />
            <h2 id="curtain-title">{TEAM_LABEL[game.turn]} to move</h2>
            <p>
              Pass the screen to {TEAM_LABEL[game.turn]}. The board shows only what their pieces can
              see.
            </p>
            <button type="button" className="btn btn-primary" onClick={s.lift} autoFocus>
              Show {TEAM_LABEL[game.turn]}’s view
            </button>
          </div>
        </div>
      )}
      <section className="game-boardArea" aria-label="Board">
        <HalvardBoard
          heights={game.heights}
          pieces={game.pieces}
          ply={game.ply}
          hints={hints}
          {...(visible ? { visible } : {})}
          summits={summitMarks(game.config.summitMode)}
          selectedId={s.selectedId}
          cursor={s.cursor}
          lastMove={lastMove}
          view={s.view}
          facing={facing}
          reducedMotion={reducedMotion}
          label={`${mountainName(s.mountain)}. ${status}. Use the arrow keys to move, Enter to select.`}
          onSquareActivate={s.activate}
          onCursorChange={s.setCursor}
          onCancel={() => s.select(null)}
        />
        {result && (
          <div className="game-banner" role="alert">
            <p className="game-bannerTitle">{result.title}</p>
            <p className="game-bannerDetail">{result.detail}</p>
            <div className="game-row">
              <button type="button" className="btn btn-primary" onClick={() => s.start(s.mountain)}>
                Replay this mountain
              </button>
              <button type="button" className="btn" onClick={onNewMountain}>
                New mountain
              </button>
            </div>
          </div>
        )}
      </section>

      <aside className={`game-panel ${sheetOpen ? 'game-open' : ''}`} aria-label="Game controls">
        <button
          type="button"
          className="game-sheetHandle"
          aria-expanded={sheetOpen}
          aria-controls="game-panel-body"
          onClick={() => setSheetOpen((open) => !open)}
        >
          <span className="game-turn">
            {!result && <PieceGlyph team={game.turn} type="B" size={22} />}
            {status}
          </span>
          <span className="label">{sheetOpen ? 'Hide' : 'Controls'}</span>
        </button>

        <div id="game-panel-body" className="game-panelBody">
          <div className="game-section">
            <p className="game-turnDesktop">
              {!result && <PieceGlyph team={game.turn} type="B" size={24} />}
              <span>{status}</span>
              <span className="label">Move {game.moveNo}</span>
            </p>
            <p className="label">{mountainName(s.mountain)}</p>
            {anyBot && (
              <ul className="game-players" aria-label="Players">
                {(['I', 'B'] as const).map((team) => (
                  <li key={team}>
                    <PieceGlyph team={team} type="B" size={18} />
                    <span>{TEAM_LABEL[team]}</span>
                    <span className="game-playerName">{playerLabel(s.players, team)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {panelExtra}

          <div className="game-section">
            {selected ? (
              <>
                <p className="game-pieceTitle">
                  <PieceGlyph team={selected.team} type={selected.type} size={28} />
                  <span>
                    {pieceName(selected)}{' '}
                    <span className="mono">{squareName(selected.r, selected.c)}</span>
                  </span>
                </p>
                <p className="game-rule">{PIECE_RULES[selected.type]}</p>
                {selected.type === 'M' && canActWithSelected && (
                  <div className="segmented" role="group" aria-label="Mason action">
                    {MASON_MODES.map((mode) => (
                      <button
                        key={mode.id}
                        type="button"
                        aria-pressed={s.masonMode === mode.id}
                        onClick={() => s.setMasonMode(mode.id)}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="game-hint">
                {over ? 'Game over.' : 'Select a piece to see where it can go.'}
              </p>
            )}
          </div>

          {s.drawOffer && !over && (
            <div className="game-section" role="group" aria-label="Draw offer">
              <p>
                {TEAM_LABEL[s.drawOffer]} offers a draw.{' '}
                {TEAM_LABEL[s.drawOffer === 'I' ? 'B' : 'I']}?
              </p>
              <div className="game-row">
                <button type="button" className="btn btn-small" onClick={() => s.answerDraw(true)}>
                  Accept
                </button>
                <button type="button" className="btn btn-small" onClick={() => s.answerDraw(false)}>
                  Decline
                </button>
              </div>
            </div>
          )}

          <div className={`game-section game-row`}>
            <button
              type="button"
              className="btn btn-small"
              onClick={s.undo}
              disabled={s.history.length === 0 || botsOnly}
            >
              Undo
            </button>
            {/* Bots do not negotiate draws (yet). */}
            {!anyBot && (
              <button
                type="button"
                className="btn btn-small"
                onClick={s.offerDraw}
                disabled={over || s.drawOffer !== null}
              >
                Offer draw
              </button>
            )}
            {botsOnly ? null : confirmResign ? (
              <button
                type="button"
                className="btn btn-small"
                onClick={() => {
                  s.resign();
                  setConfirmResign(false);
                }}
                onBlur={() => setConfirmResign(false)}
              >
                Confirm: {TEAM_LABEL[resigning]} resigns
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setConfirmResign(true)}
                disabled={over}
              >
                Resign
              </button>
            )}
          </div>

          <div className="game-section">
            <h2 className="label">Moves</h2>
            {moves.length === 0 ? (
              <p className="game-hint">No moves yet.</p>
            ) : (
              <ol className="game-moves" ref={moveList} aria-label="Move list">
                {moves.map((row) => (
                  <li key={row.no}>
                    <span className="game-moveNo">{row.no}.</span>
                    <span>{row.ivory ?? '…'}</span>
                    <span>{row.basalt ?? ''}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="game-section">
            <h2 className="label">Captured</h2>
            {(['I', 'B'] as const).map((team) => (
              <p key={team} className="game-captured">
                <span className="game-capturedTeam">{TEAM_LABEL[team]}</span>
                {captured(team).length === 0 ? (
                  <span className="game-hint">none</span>
                ) : (
                  captured(team).map((p) => (
                    <span key={p.id} title={pieceName(p)}>
                      <PieceGlyph team={p.team} type={p.type} size={22} />
                      <span className="visually-hidden">{pieceName(p)}</span>
                    </span>
                  ))
                )}
              </p>
            ))}
          </div>

          {s.game.config.fog && (
            <div className="game-section">
              <h2 className="label">Fog of war</h2>
              <p className="game-hint">
                {fogged && viewer
                  ? `You see what ${TEAM_LABEL[viewer]}’s pieces see. Dark squares are out of sight and show the height last seen.`
                  : 'The whole board is shown.'}
              </p>
              {s.perspective === 'turn' && (
                <label className="game-check">
                  <input
                    type="checkbox"
                    checked={s.hideBetweenTurns}
                    onChange={(e) => s.setHideBetweenTurns(e.target.checked)}
                  />
                  Hide the board between turns
                </label>
              )}
              <label className="game-check">
                <input
                  type="checkbox"
                  checked={s.revealAll}
                  onChange={(e) => s.setRevealAll(e.target.checked)}
                />
                Show everything (test)
              </label>
            </div>
          )}

          <div className="game-section">
            <h2 className="label">View</h2>
            <div className="segmented" role="group" aria-label="Camera view">
              {VIEWS.map((view) => (
                <button
                  key={view.id}
                  type="button"
                  aria-pressed={s.view === view.id}
                  onClick={() => s.setView(view.id)}
                >
                  {view.label}
                </button>
              ))}
            </div>
          </div>

          <div className={`game-section game-row`}>
            <button type="button" className="btn btn-small" onClick={onNewMountain}>
              New mountain
            </button>
            <button type="button" className="btn btn-small" onClick={() => s.start(s.mountain)}>
              Replay this mountain
            </button>
            <button type="button" className="btn btn-small" onClick={onClassic}>
              Classic
            </button>
          </div>

          <div className="game-section">
            <h2 className="label">Experimental rules</h2>
            <div className="game-row">
              {EXPERIMENTS.map((e) => (
                <button
                  key={e.key}
                  type="button"
                  className="btn btn-small game-toggle"
                  aria-pressed={isOn(s.mountain, e.key)}
                  onClick={() => onToggleExperiment(e.key)}
                  title={e.description}
                >
                  {e.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </aside>

      <p className="visually-hidden" role="status" aria-live="polite">
        {s.announcement}
      </p>
    </div>
  );
}
